export type RenderDebugPhase = "input" | "raf" | "render";

export interface RenderDebugStep {
  step: string;
  ms: number;
  detail: string;
  phase: RenderDebugPhase;
}

let enabled = false;
let trace: RenderDebugStep[] = [];
let pointerTraceStartedAt = 0;
let lastPointerEventDetail = "";

export function setRenderDebugEnabled(value: boolean): void {
  enabled = value;
  if (!value) {
    trace = [];
    pointerTraceStartedAt = 0;
    lastPointerEventDetail = "";
  }
}

export function isRenderDebugEnabled(): boolean {
  return enabled;
}

export function beginPointerEventTrace(): void {
  if (!enabled) {
    return;
  }
  trace = [];
  pointerTraceStartedAt = performance.now();
  lastPointerEventDetail = "";
}

export function endPointerEventTrace(eventName: string, detail = ""): void {
  if (!enabled || pointerTraceStartedAt <= 0) {
    return;
  }

  const totalMs = performance.now() - pointerTraceStartedAt;
  lastPointerEventDetail = detail;
  recordDebugStep(`${eventName} handler (sync)`, totalMs, detail, "input");
  pointerTraceStartedAt = 0;

  if (totalMs >= 8) {
    flushDebugTrace(`${eventName}${detail ? ` · ${detail}` : ""}`, totalMs);
  }
}

export function beginPointerMoveTrace(): void {
  beginPointerEventTrace();
}

export function endPointerMoveTrace(detail = ""): void {
  endPointerEventTrace("pointermove", detail);
}

export function beginPointerDownTrace(): void {
  beginPointerEventTrace();
}

export function endPointerDownTrace(detail = ""): void {
  endPointerEventTrace("pointerdown", detail);
}

export function beginPointerUpTrace(): void {
  beginPointerEventTrace();
}

export function endPointerUpTrace(detail = ""): void {
  endPointerEventTrace("pointerup", detail);
}

export function beginPointerCancelTrace(): void {
  beginPointerEventTrace();
}

export function endPointerCancelTrace(detail = ""): void {
  endPointerEventTrace("pointercancel", detail);
}

export function recordDebugStep(step: string, ms: number, detail: string, phase: RenderDebugPhase): void {
  if (!enabled) {
    return;
  }
  trace.push({ step, ms: round(ms), detail, phase });
}

export function timeDebugStep<T>(step: string, phase: RenderDebugPhase, detail: string, run: () => T): T {
  if (!enabled) {
    return run();
  }

  const start = performance.now();
  try {
    return run();
  } finally {
    recordDebugStep(step, performance.now() - start, detail, phase);
  }
}

export function wrapRafCallback<T>(label: string, run: () => T): T {
  if (!enabled) {
    return run();
  }

  const start = performance.now();
  try {
    return run();
  } finally {
    const ms = performance.now() - start;
    recordDebugStep(label, ms, "requestAnimationFrame handler", "raf");
    if (ms >= 8) {
      flushDebugTrace(label, ms);
    }
  }
}

export function takeDebugTrace(): RenderDebugStep[] {
  const snapshot = trace.slice();
  trace = [];
  return snapshot;
}

export function peekDebugTrace(): RenderDebugStep[] {
  return trace.slice();
}

export function getLastPointerEventDetail(): string {
  return lastPointerEventDetail;
}

/** @deprecated Use getLastPointerEventDetail */
export function getLastPointerMoveDetail(): string {
  return lastPointerEventDetail;
}

export function flushDebugTrace(source: string, totalMs?: number): void {
  if (!enabled || trace.length === 0) {
    return;
  }

  const rows = trace.map(step => ({
    step: step.step,
    ms: step.ms,
    phase: step.phase,
    detail: step.detail,
  }));

  const header = `[perf] ${source}${totalMs !== undefined ? ` · ${round(totalMs).toFixed(2)}ms` : ""}`;
  const effectiveMs = totalMs ?? trace.reduce((sum, step) => sum + step.ms, 0);
  const log = effectiveMs >= 16 ? console.warn : console.groupCollapsed;
  log(header);
  console.table(rows);
  if (log === console.groupCollapsed) {
    console.groupEnd();
  }
  trace = [];
}

function round(ms: number): number {
  return Math.round(ms * 100) / 100;
}
